/**
 * Analytics — thin wrapper around PostHog. Initialized once at app
 * boot if VITE_POSTHOG_KEY is set; otherwise every call is a silent
 * no-op, so dev builds and self-hosted forks don't need credentials.
 *
 * Use `track('event_name', { …props })` from anywhere in the app.
 * Mirror of the events list in the launch playbook so funnels in
 * PostHog line up with the codebase.
 */
import posthog from 'posthog-js';

// PostHog "Project Token" — write-only, public, safe to commit per
// PostHog docs ("for use in client libraries, safe to use in public
// apps"). Override via VITE_POSTHOG_KEY for forks / dev environments.
const KEY = (import.meta.env['VITE_POSTHOG_KEY'] as string | undefined) ?? 'phc_so8NNTgMeSHy85aZ7Uu6884uU4makwK4Yadun7bK7tMq';
const HOST = (import.meta.env['VITE_POSTHOG_HOST'] as string | undefined) ?? 'https://us.i.posthog.com';

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  if (!KEY) return; // disabled in dev / when env var missing
  try {
    posthog.init(KEY, {
      api_host: HOST,
      // Auto-pageview on init + on history-pushState. SPA-friendly.
      capture_pageview: true,
      // Identifies users by anonymous id until we call identify().
      // Privacy: NO session recording, NO heatmaps, NO autocapture.
      // We send only the explicit events below.
      autocapture: false,
      disable_session_recording: true,
      // Don't honor DNT — Brave/Firefox enable it by default which would
      // silently drop ALL events. We're collecting our own first-party
      // analytics, no third-party sharing, so DNT-respect would just blind
      // us to ~half of users with no upside.
      respect_dnt: false,
      // Persist via localStorage; cookieless mode keeps GDPR friendly.
      persistence: 'localStorage',
      // PostHog's bundled feature-flag client adds bytes; we don't use it.
      advanced_disable_feature_flags: true,
      loaded: () => {
        // One-line console signal so you can confirm in devtools that
        // analytics actually initialized (vs being blocked by uBlock,
        // Brave Shields, etc.).
        console.info('[analytics] posthog ready');
      },
    });
    initialized = true;
  } catch (e) {
    // Network blocked, ad-blocker, etc. — fail silently.
    console.warn('[analytics] init failed', e);
  }
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.capture(event, props);
  } catch { /* */ }
}

/** Tie events to a stable charId so PostHog funnels can deduplicate
 *  across devices + sessions. Call this whenever the active character
 *  changes (login, character switch). */
export function identifyCharacter(charId: string, traits?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.identify(charId, traits);
  } catch { /* */ }
}

/** Forget the current user identity (e.g. release character). */
export function resetIdentity(): void {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch { /* */ }
}
